import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  fetchEventsByOrg,
  fetchEventByCode,
  deleteEvent,
} from "../Api/ViewEvent";
import { Container, Table, Button, Modal, Row, Col } from "react-bootstrap";
import { toast } from "react-toastify";
import moment from "moment";

import { getAllEventTypes } from "../Api/Event";
import { getDepartmentsByOrgId } from "../Api/DepartmentOrgMaster";
import { getAllCompanyCodes } from "../Api/CompanyLocationMaster";
import { fetchCostCenters } from "../Api/ResourceMaster";
import { getResourcesByOrgId } from "../Api/ResourceList";
import { fetchCustomersByOrgId } from "../Api/InvoiceApi";

import {
  FaTrash,
  FaEye,
  FaEdit,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaUser,
  FaBuilding,
  FaMoneyBillWave,
  FaInfoCircle,
  FaTimesCircle,
  FaCheckCircle,
  FaClock,
  FaCalendarWeek,
  FaFilter,
  FaTimes,
} from "react-icons/fa";
import {
  MdEvent,
  MdCategory,
  MdPeople,
  MdBusinessCenter,
} from "react-icons/md";

import "./EventManagement.css";

const EventManagement = () => {
  const navigate = useNavigate();
  const userDetails = JSON.parse(localStorage.getItem("userDetails"));
  const orgId = userDetails?.organisationId;

  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [eventTypeMap, setEventTypeMap] = useState({});

  const [departmentMap, setDepartmentMap] = useState({});
  const [companyMap, setCompanyMap] = useState({});
  const [costCenterMap, setCostCenterMap] = useState({});
  const [resourceMap, setResourceMap] = useState({});
  const [customerMap, setCustomerMap] = useState({});

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [eventToDelete, setEventToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");

  // Date filter states
  const [dateFilterType, setDateFilterType] = useState("all"); // 'all', 'single', 'range'
  const [singleDate, setSingleDate] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [showDateFilters, setShowDateFilters] = useState(false);

  useEffect(() => {
    loadEvents();
    loadEventTypes();
    loadMaps();
  }, []);

  const loadEvents = async () => {
    try {
      const data = await fetchEventsByOrg(orgId);
      setEvents(data);
    } catch (error) {
      toast.error("Error fetching events");
    }
  };

  const handleView = async (eventCode) => {
    try {
      const data = await fetchEventByCode(orgId, eventCode);
      setSelectedEvent(data);
      setShowModal(true);
    } catch (error) {
      toast.error("Error fetching event details");
    }
  };

  const handleDeleteClick = (event) => {
    setEventToDelete(event);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!eventToDelete) return;

    setDeleting(true);
    try {
      await deleteEvent(orgId, eventToDelete.eventCode, userDetails?.userId);
      toast.success("Event deleted successfully");
      setShowDeleteConfirm(false);
      loadEvents();
    } catch (error) {
      toast.error(error.message || "Failed to delete event");
    } finally {
      setDeleting(false);
      setEventToDelete(null);
    }
  };

  const loadEventTypes = async () => {
    try {
      const data = await getAllEventTypes();
      const map = {};
      data.forEach((item) => {
        map[item.eventTypeGuid] = item.typeName;
      });
      setEventTypeMap(map);
    } catch {
      toast.error("Error loading event types");
    }
  };

  const loadMaps = async () => {
    try {
      const deptData = await getDepartmentsByOrgId(orgId);
      const deptMap = {};
      deptData.forEach((dept) => {
        deptMap[dept.guid] = dept.deptDescription;
      });
      setDepartmentMap(deptMap);

      const companyData = await getAllCompanyCodes(orgId);
      const companyMap = {};
      companyData.forEach((company) => {
        companyMap[company.companyCode] = company.name;
      });
      setCompanyMap(companyMap);

      const costCenterData = await fetchCostCenters();
      const costCenterMap = {};
      costCenterData.forEach((center) => {
        costCenterMap[center.code] = center.description;
      });
      setCostCenterMap(costCenterMap);

      const resourceData = await getResourcesByOrgId(orgId);
      const resourceMap = {};
      resourceData.forEach((resource) => {
        resourceMap[resource.resourceId] = resource.name;
      });
      setResourceMap(resourceMap);

      const customerData = await fetchCustomersByOrgId(orgId);
      const customerMap = {};
      customerData.forEach((customer) => {
        customerMap[customer.customerCode] = customer.name;
      });
      setCustomerMap(customerMap);
    } catch (error) {
      console.error("Error loading maps:", error);
    }
  };

  const getDisplayValue = (value, map, defaultValue = "-") => {
    if (!value) return defaultValue;
    const displayName = map[value];
    return displayName ? `${displayName}` : value;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      planned: { icon: <FaClock />, class: "xrs_status_planned" },
      ongoing: { icon: <FaCheckCircle />, class: "xrs_status_ongoing" },
      completed: { icon: <FaCheckCircle />, class: "xrs_status_completed" },
      cancelled: { icon: <FaTimesCircle />, class: "xrs_status_cancelled" },
    };
    const config = statusConfig[status?.toLowerCase()] || {
      icon: <MdEvent />,
      class: "xrs_status_default",
    };
    return (
      <span className={`xrs_status_badge ${config.class}`}>
        {config.icon}
        <span>{status || "Unknown"}</span>
      </span>
    );
  };

  // Date filter function
  const filterByDate = (eventDate) => {
    if (dateFilterType === "all") return true;

    const eventDateObj = moment(eventDate);

    if (dateFilterType === "single" && singleDate) {
      return eventDateObj.isSame(moment(singleDate), "day");
    }

    if (dateFilterType === "range" && startDate && endDate) {
      return eventDateObj.isBetween(
        moment(startDate),
        moment(endDate),
        "day",
        "[]",
      );
    }

    return true;
  };

  // Clear all date filters
  const clearDateFilters = () => {
    setDateFilterType("all");
    setSingleDate("");
    setStartDate("");
    setEndDate("");
    setShowDateFilters(false);
  };

  const filteredEvents = events.filter((event) => {
    const matchesSearch =
      event.eventName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      event.eventCode?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter =
      filterStatus === "all" ||
      event.eventStatus?.toLowerCase() === filterStatus;
    const matchesDate = filterByDate(event.eventDate);

    return matchesSearch && matchesFilter && matchesDate;
  });

  const stats = {
    total: events.length,
    ongoing: events.filter((e) => e.eventStatus?.toLowerCase() === "ongoing")
      .length,
    completed: events.filter(
      (e) => e.eventStatus?.toLowerCase() === "completed",
    ).length,
    planned: events.filter((e) => e.eventStatus?.toLowerCase() === "planned")
      .length,
  };

  return (
    <div className="xrs_event_container">
      <Container fluid className="xrs_event_wrapper">
        {/* Header Section */}
        <div className="xrs_event_header">
          <div className="xrs_header_content">
            <div className="xrs_header_title">
              <MdEvent className="xrs_header_icon" />
              <div>
                <h1 className="xrs_main_title">Event Management</h1>
                <p className="xrs_subtitle">
                  Manage and track all your corporate events
                </p>
              </div>
            </div>
            <Button
              className="xrs_create_btn"
              onClick={() => navigate("/create-event")}
            >
              + Create New Event
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="xrs_stats_grid">
          <div className="xrs_stat_card xrs_stat_total">
            <div className="xrs_stat_icon">
              <MdEvent />
            </div>
            <div className="xrs_stat_info">
              <h3>{stats.total}</h3>
              <p>Total Events</p>
            </div>
          </div>
          <div className="xrs_search_box">
            <input
              type="text"
              placeholder="Search by event name or code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="xrs_search_input"
            />
          </div>
        </div>

        {/* Filters */}
        <div className="xrs_filters_bar">
          <button
            className={`xrs_date_filter_toggle ${showDateFilters ? "active" : ""}`}
            onClick={() => setShowDateFilters(!showDateFilters)}
          >
            <FaCalendarWeek /> Date Filter
            {dateFilterType !== "all" && (
              <span className="xrs_filter_badge">●</span>
            )}
          </button>
          {showDateFilters && (
            <div className="xrs_date_filter_panel">
              <div className="xrs_date_filter_header">
                {/* <h6>
                <FaCalendarWeek /> Filter by Event Date
              </h6> */}
                <button
                  className="xrs_clear_dates_btn"
                  onClick={clearDateFilters}
                >
                  <FaTimes /> Clear
                </button>
              </div>

              <div className="xrs_date_filter_options">
                <label className="xrs_date_option">
                  <input
                    type="radio"
                    name="dateFilterType"
                    value="all"
                    checked={dateFilterType === "all"}
                    onChange={() => setDateFilterType("all")}
                  />
                  <span>All Dates</span>
                </label>

                <label className="xrs_date_option">
                  <input
                    type="radio"
                    name="dateFilterType"
                    value="single"
                    checked={dateFilterType === "single"}
                    onChange={() => setDateFilterType("single")}
                  />
                  <span>Single Date</span>
                </label>

                <label className="xrs_date_option">
                  <input
                    type="radio"
                    name="dateFilterType"
                    value="range"
                    checked={dateFilterType === "range"}
                    onChange={() => setDateFilterType("range")}
                  />
                  <span>Date Range</span>
                </label>
              </div>

              {dateFilterType === "single" && (
                <div className="xrs_single_date_input">
                  <label>Select Date:</label>
                  <input
                    type="date"
                    value={singleDate}
                    onChange={(e) => setSingleDate(e.target.value)}
                    className="xrs_date_input"
                  />
                </div>
              )}

              {dateFilterType === "range" && (
                <div className="xrs_date_range_inputs">
                  <div className="xrs_date_range_group">
                    <label>Start Date:</label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="xrs_date_input"
                    />
                  </div>
                  <div className="xrs_date_range_group">
                    <label>End Date:</label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="xrs_date_input"
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Date Filter Panel */}

        {/* Active Filters Display */}
        {(dateFilterType !== "all" || filterStatus !== "all" || searchTerm) && (
          <div className="xrs_active_filters">
            <span className="xrs_active_filter_label">Active Filters:</span>
            {searchTerm && (
              <span className="xrs_filter_tag">
                Search: {searchTerm}
                <button onClick={() => setSearchTerm("")}>×</button>
              </span>
            )}
            {filterStatus !== "all" && (
              <span className="xrs_filter_tag">
                Status: {filterStatus}
                <button onClick={() => setFilterStatus("all")}>×</button>
              </span>
            )}
            {dateFilterType === "single" && singleDate && (
              <span className="xrs_filter_tag">
                Date: {moment(singleDate).format("DD MMM YYYY")}
                <button
                  onClick={() => {
                    setDateFilterType("all");
                    setSingleDate("");
                  }}
                >
                  ×
                </button>
              </span>
            )}
            {dateFilterType === "range" && startDate && endDate && (
              <span className="xrs_filter_tag">
                Range: {moment(startDate).format("DD MMM")} -{" "}
                {moment(endDate).format("DD MMM YYYY")}
                <button
                  onClick={() => {
                    setDateFilterType("all");
                    setStartDate("");
                    setEndDate("");
                  }}
                >
                  ×
                </button>
              </span>
            )}
            <span className="xrs_filter_result_count">
              {filteredEvents.length} results found
            </span>
          </div>
        )}

        {/* Events Table */}
        {filteredEvents.length > 0 ? (
          <div className="xrs_table_wrapper">
            <table className="xrs_event_table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Event Code</th>
                  <th>Event Name</th>
                  <th>Type</th>
                  <th>Date</th>
                  <th>Location</th>
                  <th>Department</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredEvents.map((event, index) => (
                  <tr key={event.eventCode} className="xrs_table_row">
                    <td className="xrs_table_cell">{index + 1}</td>
                    <td className="xrs_table_cell xrs_event_code">
                      {event.eventCode}
                    </td>
                    <td className="xrs_table_cell xrs_event_name">
                      {event.eventName}
                    </td>
                    <td className="xrs_table_cell">
                      <span className="xrs_event_type_badge">
                        {eventTypeMap[event.eventType] || event.eventType}
                      </span>
                    </td>
                    <td className="xrs_table_cell">
                      <div className="xrs_date_cell">
                        <FaCalendarAlt className="xrs_date_icon" />
                        {moment(event.eventDate).format("DD MMM, YYYY")}
                      </div>
                    </td>
                    <td className="xrs_table_cell">
                      <div className="xrs_location_cell">
                        <FaMapMarkerAlt className="xrs_location_icon" />
                        {event.eventLocation || "-"}
                      </div>
                    </td>
                    <td className="xrs_table_cell">
                      {getDisplayValue(event.department, departmentMap)}
                    </td>
                    <td className="xrs_table_cell">
                      {getStatusBadge(event.eventStatus)}
                    </td>
                    <td className="xrs_table_cell">
                      <div className="xrs_action_buttons">
                        <button
                          className="xrs_action_btn xrs_view_btn"
                          onClick={() => handleView(event.eventCode)}
                          title="View Details"
                        >
                          <FaEye />
                        </button>
                        <button
                          className="xrs_action_btn xrs_edit_btn"
                          onClick={() =>
                            navigate(`/editEvent/${event.eventCode}`)
                          }
                          title="Edit Event"
                        >
                          <FaEdit />
                        </button>
                        <button
                          className="xrs_action_btn xrs_delete_btn"
                          onClick={() => handleDeleteClick(event)}
                          disabled={deleting}
                          title="Delete Event"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="xrs_empty_state">
            <MdEvent className="xrs_empty_icon" />
            <h3>No Events Found</h3>
            <p>Try adjusting your filters or create a new event</p>
            <Button
              className="xrs_create_btn"
              onClick={() => {
                clearDateFilters();
                setFilterStatus("all");
                setSearchTerm("");
              }}
            >
              Clear All Filters
            </Button>
          </div>
        )}

        {/* Rest of your modals remain the same */}
        {/* Event Details Modal */}
        <Modal
          show={showModal}
          onHide={() => setShowModal(false)}
          size="lg"
          className="xrs_modal"
          centered
        >
          <Modal.Header closeButton className="xrs_modal_header">
            <Modal.Title>
              <div className="xrs_modal_title">
                <MdEvent className="xrs_modal_icon" />
                Event Details
              </div>
            </Modal.Title>
          </Modal.Header>

          <Modal.Body className="xrs_modal_body">
            {selectedEvent && (
              <div>
                <div className="xrs_event_header_section">
                  <h2 className="xrs_event_name_display">
                    {selectedEvent.eventName}
                  </h2>
                  <div className="xrs_event_code_display">
                    Code: {selectedEvent.eventCode}
                  </div>
                </div>

                <div className="xrs_details_grid">
                  <div className="xrs_details_section">
                    <h4 className="xrs_section_title">
                      <FaInfoCircle className="xrs_section_icon" />
                      Basic Information
                    </h4>
                    <div className="xrs_detail_item">
                      <strong>Event Type:</strong>
                      <span>
                        {eventTypeMap[selectedEvent.eventType] ||
                          selectedEvent.eventType}
                      </span>
                    </div>
                    <div className="xrs_detail_item">
                      <strong>Event Date:</strong>
                      <span>
                        {moment(selectedEvent.eventDate).format(
                          "DD MMMM, YYYY",
                        )}
                      </span>
                    </div>
                    <div className="xrs_detail_item">
                      <strong>End Date:</strong>
                      <span>
                        {selectedEvent.eventEndDate
                          ? moment(selectedEvent.eventEndDate).format(
                              "DD MMMM, YYYY",
                            )
                          : "-"}
                      </span>
                    </div>
                    <div className="xrs_detail_item">
                      <strong>Start Time:</strong>
                      <span>
                        {selectedEvent.eventStartTime
                          ? selectedEvent.eventStartTime.slice(0, 5)
                          : "-"}
                      </span>
                    </div>
                    <div className="xrs_detail_item">
                      <strong>End Time:</strong>
                      <span>
                        {selectedEvent.eventEndTime
                          ? selectedEvent.eventEndTime.slice(0, 5)
                          : "-"}
                      </span>
                    </div>
                    <div className="xrs_detail_item">
                      <strong>Location:</strong>
                      <span>{selectedEvent.eventLocation || "-"}</span>
                    </div>
                    <div className="xrs_detail_item">
                      <strong>Organizer:</strong>
                      <span>{selectedEvent.organizerName || "-"}</span>
                    </div>
                  </div>

                  <div className="xrs_details_section">
                    <h4 className="xrs_section_title">
                      <MdBusinessCenter className="xrs_section_icon" />
                      Organizational Details
                    </h4>
                    <div className="xrs_detail_item">
                      <strong>Department:</strong>
                      <span>
                        {getDisplayValue(
                          selectedEvent.department,
                          departmentMap,
                        )}
                      </span>
                    </div>
                    <div className="xrs_detail_item">
                      <strong>Company Code:</strong>
                      <span>
                        {getDisplayValue(
                          selectedEvent.eventCompanyCode,
                          companyMap,
                        )}
                      </span>
                    </div>
                    <div className="xrs_detail_item">
                      <strong>Cost Center:</strong>
                      <span>
                        {getDisplayValue(
                          selectedEvent.eventCostCenter,
                          costCenterMap,
                        )}
                      </span>
                    </div>
                    <div className="xrs_detail_item">
                      <strong>Resource:</strong>
                      <span>
                        {getDisplayValue(
                          selectedEvent.eventResource,
                          resourceMap,
                        )}
                      </span>
                    </div>
                    <div className="xrs_detail_item">
                      <strong>Event Client:</strong>
                      <span>
                        {getDisplayValue(
                          selectedEvent.eventClient,
                          customerMap,
                        )}
                      </span>
                    </div>
                    <div className="xrs_detail_item">
                      <strong>Budget Allocation:</strong>
                      <span className="xrs_budget">
                        {selectedEvent.budgetAllocation
                          ? `$${selectedEvent.budgetAllocation}`
                          : "-"}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="xrs_details_section_full">
                  <h4 className="xrs_section_title">
                    <FaInfoCircle className="xrs_section_icon" />
                    Description
                  </h4>
                  <p className="xrs_description_text">
                    {selectedEvent.eventDescription ||
                      "No description provided"}
                  </p>
                </div>

                <div className="xrs_status_section">
                  <div className="xrs_status_item">
                    <strong>Approval Status:</strong>
                    {getStatusBadge(selectedEvent.approvalStatus)}
                  </div>
                  <div className="xrs_status_item">
                    <strong>Event Status:</strong>
                    {getStatusBadge(selectedEvent.eventStatus)}
                  </div>
                </div>
              </div>
            )}
          </Modal.Body>

          <Modal.Footer className="xrs_modal_footer">
            <Button
              variant="secondary"
              onClick={() => setShowModal(false)}
              className="xrs_close_btn"
            >
              Close
            </Button>
            <Button
              variant="warning"
              onClick={() => {
                setShowModal(false);
                navigate(`/editEvent/${selectedEvent?.eventCode}`);
              }}
              className="xrs_edit_modal_btn"
            >
              <FaEdit /> Edit Event
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal
          show={showDeleteConfirm}
          onHide={() => setShowDeleteConfirm(false)}
          className="xrs_modal xrs_delete_modal"
          centered
        >
          <Modal.Header
            closeButton
            className="xrs_modal_header xrs_delete_header"
          >
            <Modal.Title>
              <div className="xrs_modal_title">
                <FaTimesCircle className="xrs_modal_icon xrs_delete_icon" />
                Confirm Deletion
              </div>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="xrs_modal_body">
            <div className="xrs_delete_content">
              <p>Are you sure you want to delete event</p>
              <strong className="xrs_delete_event_name">
                "{eventToDelete?.eventName}"
              </strong>
              <p className="xrs_delete_warning">
                This action can be reversed by an administrator.
              </p>
            </div>
          </Modal.Body>
          <Modal.Footer className="xrs_modal_footer">
            <Button
              variant="secondary"
              onClick={() => setShowDeleteConfirm(false)}
              className="xrs_cancel_btn"
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={confirmDelete}
              disabled={deleting}
              className="xrs_confirm_delete_btn"
            >
              {deleting ? "Deleting..." : "Delete Event"}
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </div>
  );
};

export default EventManagement;

// import { useEffect, useState } from "react";
// import { useNavigate } from "react-router-dom";
// import {
//   fetchEventsByOrg,
//   fetchEventByCode,
//   deleteEvent,
// } from "../Api/ViewEvent";
// import { Container, Table, Button, Modal, Row, Col } from "react-bootstrap";
// import { toast } from "react-toastify";
// import moment from "moment";

// import { getAllEventTypes } from "../Api/Event";
// import { getDepartmentsByOrgId } from "../Api/DepartmentOrgMaster";
// import { getAllCompanyCodes } from "../Api/CompanyLocationMaster";
// import { fetchCostCenters } from "../Api/ResourceMaster";
// import { getResourcesByOrgId } from "../Api/ResourceList";
// import { fetchCustomersByOrgId } from "../Api/InvoiceApi";

// import { FaTrash } from "react-icons/fa";

// const ViewEvent = () => {
//   const navigate = useNavigate();
//   const userDetails = JSON.parse(localStorage.getItem("userDetails"));
//   const orgId = userDetails?.organisationId;

//   const [events, setEvents] = useState([]);
//   const [selectedEvent, setSelectedEvent] = useState(null);
//   const [showModal, setShowModal] = useState(false);
//   const [eventTypeMap, setEventTypeMap] = useState({});

//   // Maps for displaying names instead of codes
//   const [departmentMap, setDepartmentMap] = useState({});
//   const [companyMap, setCompanyMap] = useState({});
//   const [costCenterMap, setCostCenterMap] = useState({});
//   const [resourceMap, setResourceMap] = useState({});
//   const [customerMap, setCustomerMap] = useState({});

//   const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
//   const [eventToDelete, setEventToDelete] = useState(null);
//   const [deleting, setDeleting] = useState(false);

//   useEffect(() => {
//     loadEvents();
//     loadEventTypes();
//     loadMaps();
//   }, []);

//   const loadEvents = async () => {
//     try {
//       const data = await fetchEventsByOrg(orgId);
//       setEvents(data);
//     } catch (error) {
//       toast.error("Error fetching events");
//     }
//   };

//   const handleView = async (eventCode) => {
//     try {
//       const data = await fetchEventByCode(orgId, eventCode);
//       setSelectedEvent(data);
//       setShowModal(true);
//     } catch (error) {
//       toast.error("Error fetching event details");
//     }
//   };

//   // Add delete handler
//   const handleDeleteClick = (event) => {
//     setEventToDelete(event);
//     setShowDeleteConfirm(true);
//   };

//   const confirmDelete = async () => {
//     if (!eventToDelete) return;

//     setDeleting(true);
//     try {
//       await deleteEvent(orgId, eventToDelete.eventCode, userDetails?.userId);
//       toast.success("Event deleted successfully");
//       setShowDeleteConfirm(false);
//       loadEvents(); // Refresh the list
//     } catch (error) {
//       toast.error(error.message || "Failed to delete event");
//     } finally {
//       setDeleting(false);
//       setEventToDelete(null);
//     }
//   };

//   const loadEventTypes = async () => {
//     try {
//       const data = await getAllEventTypes();
//       const map = {};
//       data.forEach((item) => {
//         map[item.eventTypeGuid] = item.typeName;
//       });
//       setEventTypeMap(map);
//     } catch {
//       toast.error("Error loading event types");
//     }
//   };

//   const loadMaps = async () => {
//     try {
//       // Load Departments
//       const deptData = await getDepartmentsByOrgId(orgId);
//       const deptMap = {};
//       deptData.forEach((dept) => {
//         deptMap[dept.guid] = dept.deptDescription;
//       });
//       setDepartmentMap(deptMap);

//       // Load Companies
//       const companyData = await getAllCompanyCodes(orgId);
//       const companyMap = {};
//       companyData.forEach((company) => {
//         companyMap[company.companyCode] = company.name;
//       });
//       setCompanyMap(companyMap);

//       // Load Cost Centers
//       const costCenterData = await fetchCostCenters();
//       const costCenterMap = {};
//       costCenterData.forEach((center) => {
//         costCenterMap[center.code] = center.description;
//       });
//       setCostCenterMap(costCenterMap);

//       // Load Resources
//       const resourceData = await getResourcesByOrgId(orgId);
//       const resourceMap = {};
//       resourceData.forEach((resource) => {
//         resourceMap[resource.resourceId] = resource.name;
//       });
//       setResourceMap(resourceMap);

//       // Load Customers
//       const customerData = await fetchCustomersByOrgId(orgId);
//       const customerMap = {};
//       customerData.forEach((customer) => {
//         customerMap[customer.customerCode] = customer.name;
//       });
//       setCustomerMap(customerMap);
//     } catch (error) {
//       console.error("Error loading maps:", error);
//     }
//   };

//   const getDisplayValue = (value, map, defaultValue = "-") => {
//     if (!value) return defaultValue;
//     const displayName = map[value];
//     return displayName ? `${displayName} (${value})` : value;
//   };

//   return (
//     <div className="main-content expand">
//       <Container fluid className="list-container">
//         <h4 className="mb-3">Event List</h4>
//         {events.length > 0 ? (
//           <Table bordered hover responsive>
//             <thead>
//               <tr>
//                 <th>#</th>
//                 <th>Event Code</th>
//                 <th>Event Name</th>
//                 <th>Type</th>
//                 <th>Date</th>
//                 <th>End Date</th>
//                 <th>Location</th>
//                 <th>Organizer</th>
//                 <th>Department</th>
//                 <th>Client</th>
//                 <th>Status</th>
//                 <th>Action</th>
//               </tr>
//             </thead>

//             <tbody>
//               {events.map((event, index) => (
//                 <tr key={event.eventCode}>
//                   <td>{index + 1}</td>
//                   <td>{event.eventCode}</td>
//                   <td>{event.eventName}</td>
//                   <td>{eventTypeMap[event.eventType] || event.eventType}</td>
//                   <td>{moment(event.eventDate).format("DD-MM-YYYY")}</td>
//                   <td>
//                     {event.eventEndDate
//                       ? moment(event.eventEndDate).format("DD-MM-YYYY")
//                       : "-"}
//                   </td>
//                   <td>{event.eventLocation || "-"}</td>
//                   <td>{event.organizerName || "-"}</td>
//                   <td>{getDisplayValue(event.department, departmentMap)}</td>
//                   <td>{getDisplayValue(event.eventClient, customerMap)}</td>
//                   <td>{event.eventStatus}</td>
//                   <td style={{ display: "flex" }}>
//                     <Button
//                       size="sm"
//                       variant="info"
//                       onClick={() => handleView(event.eventCode)}
//                     >
//                       View
//                     </Button>
//                     <Button
//                       size="sm"
//                       variant="warning"
//                       className="ms-2"
//                       onClick={() => navigate(`/editEvent/${event.eventCode}`)}
//                     >
//                       Edit
//                     </Button>
//                     <Button
//                       size="sm"
//                       variant="danger"
//                       className="ms-2"
//                       onClick={() => handleDeleteClick(event)}
//                       disabled={deleting}
//                     >
//                       <FaTrash /> Delete
//                     </Button>
//                   </td>
//                 </tr>
//               ))}
//             </tbody>
//           </Table>
//         ) : (
//           <p>No events found</p>
//         )}
//         {/* EVENT DETAILS MODAL */}
//         <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
//           <Modal.Header closeButton>
//             <Modal.Title>Event Details</Modal.Title>
//           </Modal.Header>

//           <Modal.Body>
//             {selectedEvent && (
//               <div>
//                 <Row>
//                   <Col md={6}>
//                     <p>
//                       <strong>Event Code:</strong> {selectedEvent.eventCode}
//                     </p>
//                     <p>
//                       <strong>Event Name:</strong> {selectedEvent.eventName}
//                     </p>
//                     <p>
//                       <strong>Event Type:</strong>{" "}
//                       {eventTypeMap[selectedEvent.eventType] ||
//                         selectedEvent.eventType}
//                     </p>
//                     <p>
//                       <strong>Event Date:</strong>{" "}
//                       {moment(selectedEvent.eventDate).format("DD-MM-YYYY")}
//                     </p>
//                     <p>
//                       <strong>End Date:</strong>{" "}
//                       {selectedEvent.eventEndDate
//                         ? moment(selectedEvent.eventEndDate).format(
//                             "DD-MM-YYYY",
//                           )
//                         : "-"}
//                     </p>
//                     <p>
//                       <strong>Start Time:</strong>{" "}
//                       {selectedEvent.eventStartTime
//                         ? selectedEvent.eventStartTime.slice(0, 5)
//                         : "-"}
//                     </p>
//                     <p>
//                       <strong>End Time:</strong>{" "}
//                       {selectedEvent.eventEndTime
//                         ? selectedEvent.eventEndTime.slice(0, 5)
//                         : "-"}
//                     </p>
//                   </Col>
//                   <Col md={6}>
//                     <p>
//                       <strong>Location:</strong>{" "}
//                       {selectedEvent.eventLocation || "-"}
//                     </p>
//                     <p>
//                       <strong>Organizer:</strong>{" "}
//                       {selectedEvent.organizerName || "-"}
//                     </p>
//                     <p>
//                       <strong>Budget:</strong>{" "}
//                       {selectedEvent.budgetAllocation || "-"}
//                     </p>
//                     <p>
//                       <strong>Department:</strong>{" "}
//                       {getDisplayValue(selectedEvent.department, departmentMap)}
//                     </p>
//                     <p>
//                       <strong>Company Code:</strong>{" "}
//                       {getDisplayValue(
//                         selectedEvent.eventCompanyCode,
//                         companyMap,
//                       )}
//                     </p>
//                     <p>
//                       <strong>Cost Center:</strong>{" "}
//                       {getDisplayValue(
//                         selectedEvent.eventCostCenter,
//                         costCenterMap,
//                       )}
//                     </p>
//                     <p>
//                       <strong>Resource:</strong>{" "}
//                       {getDisplayValue(
//                         selectedEvent.eventResource,
//                         resourceMap,
//                       )}
//                     </p>
//                     <p>
//                       <strong>Event Client:</strong>{" "}
//                       {getDisplayValue(selectedEvent.eventClient, customerMap)}
//                     </p>
//                   </Col>
//                 </Row>

//                 <Row>
//                   <Col md={12}>
//                     <p>
//                       <strong>Description:</strong>
//                     </p>
//                     <p>{selectedEvent.eventDescription || "-"}</p>
//                   </Col>
//                 </Row>

//                 <Row>
//                   <Col md={6}>
//                     <p>
//                       <strong>Entity Type:</strong>{" "}
//                       {selectedEvent.entityType || "-"}
//                     </p>
//                   </Col>
//                   <Col md={6}>
//                     <p>
//                       <strong>Entity ID:</strong>{" "}
//                       {selectedEvent.entityId || "-"}
//                     </p>
//                   </Col>
//                 </Row>

//                 <Row>
//                   <Col md={6}>
//                     <p>
//                       <strong>Approval Status:</strong>{" "}
//                       {selectedEvent.approvalStatus}
//                     </p>
//                   </Col>
//                   <Col md={6}>
//                     <p>
//                       <strong>Event Status:</strong> {selectedEvent.eventStatus}
//                     </p>
//                   </Col>
//                 </Row>
//               </div>
//             )}
//           </Modal.Body>

//           <Modal.Footer>
//             <Button variant="secondary" onClick={() => setShowModal(false)}>
//               Close
//             </Button>
//             <Button
//               variant="warning"
//               onClick={() => {
//                 setShowModal(false);
//                 navigate(`/editEvent/${selectedEvent?.eventCode}`);
//               }}
//             >
//               Edit Event
//             </Button>
//           </Modal.Footer>
//         </Modal>

//         <Modal
//           show={showDeleteConfirm}
//           onHide={() => setShowDeleteConfirm(false)}
//         >
//           <Modal.Header closeButton>
//             <Modal.Title>Confirm Delete</Modal.Title>
//           </Modal.Header>
//           <Modal.Body>
//             Are you sure you want to delete event{" "}
//             <strong>{eventToDelete?.eventName}</strong>?
//             <br />
//             This action can be reversed by an administrator.
//           </Modal.Body>
//           <Modal.Footer>
//             <Button
//               variant="secondary"
//               onClick={() => setShowDeleteConfirm(false)}
//             >
//               Cancel
//             </Button>
//             <Button
//               variant="danger"
//               onClick={confirmDelete}
//               disabled={deleting}
//             >
//               {deleting ? "Deleting..." : "Delete"}
//             </Button>
//           </Modal.Footer>
//         </Modal>
//       </Container>
//     </div>
//   );
// };

// export default ViewEvent;
